/**
 * One row in a Settings → Documents list, with an inline rename control.
 *
 * Shared by the tenant Settings modal (identity + salary lists, which were
 * duplicated markup) and the owner portal's Documents tab, so a document looks
 * and behaves the same wherever an account sees its own files.
 *
 * Renaming edits the document's display name (both `title` and `file_name` —
 * the admin Documents view reads the latter) — the stored file and its URL
 * are untouched, so links already handed out keep working. The write goes
 * through the `rename_tenant_document` function: tenant_documents has no user
 * UPDATE policy, so a direct client update would match zero rows and report
 * success. See 20260813090000_settings_billing_address_and_document_rename.sql.
 */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Check, Download, File, Loader2, Pencil, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useLanguage } from "@/contexts/LanguageContext";
import { resolveTenantDocumentUrl } from "@/lib/tenant-document-url";
import { openPendingDocumentTab } from "@/lib/open-document-tab";

export type AccountDocument = {
  id: string;
  title?: string | null;
  file_name?: string | null;
  file_url?: string | null;
  status?: string | null;
  notes?: string | null;
  uploaded_by?: string | null;
  user_id?: string | null;
};

const MAX_TITLE_LENGTH = 120;

function useRenameDocument(onRenamed?: () => void) {
  const { t } = useLanguage();

  return useMutation({
    mutationFn: async (input: { documentId: string; title: string }) => {
      const { data, error } = await supabase.rpc("rename_tenant_document", {
        _document_id: input.documentId,
        _title: input.title,
      });
      if (error) throw error;
      return data as { id: string; title: string; file_name: string };
    },
    onSuccess: () => {
      toast.success(t("settings.documents.renamed", "Document renamed."));
      onRenamed?.();
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error
          ? error.message
          : t(
              "settings.documents.renameFailed",
              "Could not rename the document."
            )
      );
    },
  });
}

export function AccountDocumentRow({
  doc,
  onRenamed,
  onDelete,
  deleting = false,
}: {
  doc: AccountDocument;
  onRenamed?: () => void;
  onDelete?: () => void;
  deleting?: boolean;
}) {
  const { language, t } = useLanguage();
  const rename = useRenameDocument(onRenamed);
  const [openingFile, setOpeningFile] = useState(false);

  const openFile = async () => {
    if (!doc.file_url || openingFile) return;
    // Open the tab synchronously inside the click's user gesture so it isn't
    // blocked by the popup blocker, then point it at the resolved URL once
    // signed (`file_url` may be a bare or bucket-qualified Storage path —
    // e.g. handover protocols filed by COL-425 — rather than a browser URL,
    // so it has to be signed first).
    const tab = openPendingDocumentTab();
    setOpeningFile(true);
    try {
      const url = await resolveTenantDocumentUrl(doc.file_url);
      if (url) {
        tab.navigate(url);
      } else {
        tab.cancel();
        toast.error(
          language === "de"
            ? "Dokument konnte nicht geöffnet werden"
            : "Could not open the document"
        );
      }
    } finally {
      setOpeningFile(false);
    }
  };

  const displayName =
    doc.title || doc.file_name || (language === "de" ? "Dokument" : "Document");

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(displayName);

  const startEditing = () => {
    setDraft(doc.title || doc.file_name || "");
    setEditing(true);
  };

  const commit = () => {
    const next = draft.trim();
    if (!next) {
      toast.error(
        t("settings.documents.nameRequired", "Give the document a name.")
      );
      return;
    }
    if (next === (doc.title ?? "")) {
      setEditing(false);
      return;
    }
    rename.mutate(
      { documentId: doc.id, title: next },
      { onSuccess: () => setEditing(false) }
    );
  };

  const isManualAdminUpload =
    !!doc.uploaded_by &&
    !!doc.user_id &&
    String(doc.uploaded_by) !== String(doc.user_id);

  return (
    <div className="flex items-center justify-between gap-3 p-4 rounded-2xl border border-border/60 bg-background hover:bg-muted/20 transition-colors">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <File className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex items-center gap-2">
              <Input
                value={draft}
                autoFocus
                maxLength={MAX_TITLE_LENGTH}
                disabled={rename.isPending}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commit();
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setEditing(false);
                  }
                }}
                aria-label={t("settings.documents.nameLabel", "Document name")}
                className="h-9 rounded-xl"
              />
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-xl border-border/60 shrink-0"
                onClick={commit}
                disabled={rename.isPending}
                title={t("settings.save", "Save")}
              >
                {rename.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-xl shrink-0"
                onClick={() => setEditing(false)}
                disabled={rename.isPending}
                title={t("settings.cancel", "Cancel")}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {displayName}
              </p>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-lg shrink-0 text-muted-foreground hover:text-foreground"
                onClick={startEditing}
                title={t("settings.documents.rename", "Rename")}
                aria-label={t("settings.documents.rename", "Rename")}
              >
                <Pencil className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}

          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {isManualAdminUpload && (
              <Badge className="bg-sky-50 text-sky-700 border-sky-200 text-xs">
                {language === "de"
                  ? "Manuell (Admin)"
                  : "Manual upload by admin"}
              </Badge>
            )}
            {doc.status === "pending" && (
              <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                {language === "de" ? "Ausstehend" : "Pending"}
              </Badge>
            )}
            {doc.status === "approved" && (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                {language === "de" ? "Genehmigt" : "Approved"}
              </Badge>
            )}
            {doc.status === "rejected" && (
              <Badge className="bg-destructive/10 text-destructive border-destructive/20 text-xs">
                {language === "de" ? "Abgelehnt" : "Rejected"}
              </Badge>
            )}
            {doc.status === "expired" && (
              <Badge variant="secondary" className="text-xs">
                {language === "de" ? "Abgelaufen" : "Expired"}
              </Badge>
            )}
          </div>

          {doc.status === "rejected" && doc.notes && (
            <p className="text-xs text-destructive mt-1">
              {language === "de" ? "Grund" : "Reason"}: {doc.notes}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 rounded-xl border-border/60"
          disabled={openingFile}
          onClick={() => {
            if (doc.file_url) void openFile();
            else
              toast.error(
                language === "de"
                  ? "Dokument-URL nicht verfügbar"
                  : "Document URL not available"
              );
          }}
          title={language === "de" ? "Öffnen" : "Open"}
        >
          {openingFile ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
        </Button>
        {onDelete && (
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 rounded-xl border-border/60"
            onClick={onDelete}
            disabled={deleting}
            title={language === "de" ? "Entfernen" : "Remove"}
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default AccountDocumentRow;
